import type { MouseEvent, VoidFunctionComponent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { trimString } from '../../../../utils';
import type { Suggestion } from './types';

type ItemizedRuleSuggestionListItem = Omit<Suggestion, 'link'>;

type ItemizedRuleSuggestionListProps = {
  suggestions: ItemizedRuleSuggestionListItem[];
  selectRule: (id: string | null) => void;
  onExternalClick: () => void;
};

type ItemizedRulePreviewItemProps = ItemizedRuleSuggestionListItem & {
  onClick: (e: MouseEvent<HTMLDivElement>) => void;
};

const ItemizedRulePreviewItem: VoidFunctionComponent<ItemizedRulePreviewItemProps> = ({
  id,
  title,
  description,
  onClick,
}) => (
  // ignore these ,we know this is valid
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  <div onClick={onClick}>
    <span>{id}: {title}</span>
    <p><small>{trimString(description || '', 170)}</small></p>
  </div>
);

const ItemizedRuleSuggestionList: VoidFunctionComponent<ItemizedRuleSuggestionListProps> = ({
  suggestions,
  selectRule,
  onExternalClick,
}) => {
  const onRuleSelected = useCallback((e: Event, selectedRuleId: string) => {
    e.stopPropagation();
    selectRule(selectedRuleId);
  }, [selectRule]);

  const outsideClickRef = useRef(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (outsideClickRef.current && !outsideClickRef.current.contains(event.target)) {
        onExternalClick();
      }
    };
    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [outsideClickRef, onExternalClick]);

  return (
    <div className="itemized-suggestions-root">
      <div
        ref={outsideClickRef}
        className="itemized-suggestions">
        {suggestions.map(({ description, id, title }) => (
          <ItemizedRulePreviewItem
            key={id}
            id={id}
            title={title}
            description={description}
            onClick={(e: MouseEvent<HTMLDivElement, MouseEvent>) => onRuleSelected(e, id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ItemizedRuleSuggestionList;
